// getUserMedia wrapper + frame sampler. Keeps the live <video> element
// playing at native rate, but exposes a paced "tick" loop at the configured
// capture FPS that the inference pipeline consumes.

export interface CameraOptions {
  deviceId?: string;
  facingMode?: "user" | "environment";
  preferredWidth?: number;
  preferredHeight?: number;
}

export interface CameraStream {
  stream: MediaStream;
  videoElement: HTMLVideoElement;
  width: number;
  height: number;
  stop(): void;
}

export async function listCameras(): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  // Permissions are required for labels to be populated; if the user hasn't
  // granted yet, labels come back empty (still usable, just less helpful).
  const all = await navigator.mediaDevices.enumerateDevices();
  return all.filter((d) => d.kind === "videoinput");
}

export async function startCamera(
  videoElement: HTMLVideoElement,
  opts: CameraOptions = {},
): Promise<CameraStream> {
  const constraints: MediaStreamConstraints = {
    audio: false,
    video: {
      ...(opts.deviceId ? { deviceId: { exact: opts.deviceId } } : {}),
      ...(opts.facingMode && !opts.deviceId ? { facingMode: opts.facingMode } : {}),
      width: opts.preferredWidth ? { ideal: opts.preferredWidth } : { ideal: 1280 },
      height: opts.preferredHeight ? { ideal: opts.preferredHeight } : { ideal: 720 },
    },
  };
  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  videoElement.srcObject = stream;
  await videoElement.play();
  await waitFor(() => videoElement.videoWidth > 0 && videoElement.videoHeight > 0, 5000);

  return {
    stream,
    videoElement,
    width: videoElement.videoWidth,
    height: videoElement.videoHeight,
    stop() {
      stream.getTracks().forEach((t) => t.stop());
      videoElement.srcObject = null;
    },
  };
}

async function waitFor(
  predicate: () => boolean,
  timeoutMs: number,
  intervalMs = 50,
): Promise<void> {
  const start = performance.now();
  while (!predicate()) {
    if (performance.now() - start > timeoutMs) {
      throw new Error("Timed out waiting for camera to produce frames");
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

/**
 * A paced loop that calls `tick` at most `targetFps` times per second.
 * Skips a tick if the previous one is still running (so inference latency
 * gracefully degrades the effective rate instead of queueing frames).
 */
export class FrameLoop {
  private intervalMs: number;
  private timer: number | null = null;
  private inflight = false;
  private running = false;

  constructor(targetFps: number, private tick: () => Promise<void>) {
    this.intervalMs = 1000 / Math.max(targetFps, 1);
  }

  setFps(targetFps: number): void {
    this.intervalMs = 1000 / Math.max(targetFps, 1);
    if (this.running) {
      this.stop();
      this.start();
    }
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const loop = async () => {
      if (!this.running) return;
      this.timer = window.setTimeout(loop, this.intervalMs);
      if (this.inflight) return;
      this.inflight = true;
      try {
        await this.tick();
      } finally {
        this.inflight = false;
      }
    };
    loop();
  }

  stop(): void {
    this.running = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
